<template>
  <div
    class="elevation-profile"
    :class="{ collapsed: isCollapsed, expanded: isExpanded }"
  >
    <div class="profile-header pr-3">
      <button
        v-if="collapsible"
        class="collapse-btn"
        type="button"
        :title="isCollapsed ? 'Show elevation profile' : 'Hide elevation profile'"
        @click="toggleCollapse"
      >
        <span class="material-icons">{{ isCollapsed ? 'expand_less' : 'expand_more' }}</span>
      </button>

      <!-- Extra header controls supplied by the consumer (e.g. the planner's
           back-to-all-routes button) -->
      <slot name="headerLeft"></slot>

      <h3 v-if="title" class="profile-title d-none d-md-flex">
        <span class="profile-title-text" :title="title">{{ title }}</span>
        <small v-if="subtitle" class="view-subtitle" :title="subtitle">{{ subtitle }}</small>
      </h3>

      <div v-if="showStats" class="profile-stats">
        <slot name="statsHeader" :stats="resolvedStats" :unit-labels="unitLabels">
          <span class="stat-item">
            <span class="material-icons">straighten</span>
            {{ formattedDistance }}
          </span>
          <span class="stat-item">
            <span class="material-icons">trending_up</span>
            {{ formattedGain }}
          </span>
          <span class="stat-item">
            <span class="material-icons">trending_down</span>
            {{ formattedLoss }}
          </span>
          <span v-if="resolvedStats.durationSeconds != null" class="stat-item">
            <span class="material-icons">schedule</span>
            {{ formattedDuration }}
          </span>
        </slot>
        <!-- More stats from the consumer, after the standard ones (the planner's sunrise/sunset). -->
        <slot name="statsExtra"></slot>
      </div>

      <button
        v-if="expandable"
        class="expand-btn"
        type="button"
        :title="isExpanded ? 'Minimize' : 'Maximize'"
        @click="toggleExpand"
      >
        <span class="material-icons" style="font-size: 20px">{{ isExpanded ? 'fullscreen_exit' : 'fullscreen' }}</span>
      </button>
    </div>

    <div v-show="!isCollapsed" class="profile-content">
      <div class="chart-container" ref="chartContainer">
        <canvas ref="chartCanvas"></canvas>
        <!-- Highlight markers positioned along the x-axis -->
        <div
          v-for="marker in highlightMarkers"
          :key="marker.id"
          class="highlight-marker"
          :style="{ left: marker.leftPct + '%' }"
          :title="marker.name"
          @click="onHighlightClick(marker)"
          @mouseenter="onHighlightHover(marker)"
          @mouseleave="emitHoverEnd"
        >
          <span v-if="marker.icon" class="material-icons highlight-icon">{{ marker.icon }}</span>
          <span v-else class="highlight-dot"></span>
        </div>

        <!-- pinnedTooltip mode: fixed top-right tile instead of Chart.js's
             cursor-following tooltip, so hovering never covers the line. -->
        <div v-if="pinnedReadout" class="pinned-readout">
          <span v-if="pinnedReadout.routeName" class="pinned-readout__route">{{ pinnedReadout.routeName }}</span>
          <span class="pinned-readout__item">
            <span class="material-icons">straighten</span>
            {{ pinnedReadout.distanceLabel }}
          </span>
          <span class="pinned-readout__item">
            <span class="material-icons">terrain</span>
            {{ pinnedReadout.elevationLabel }}
          </span>
          <span v-if="pinnedReadout.gradeLabel" class="pinned-readout__item">
            <span class="pinned-readout__swatch" :style="{ backgroundColor: pinnedReadout.gradeColor }"></span>
            {{ pinnedReadout.gradeLabel }}
          </span>
          <span v-if="pinnedReadout.surfaceLabel" class="pinned-readout__item">
            <span class="pinned-readout__swatch" :style="{ backgroundColor: pinnedReadout.surfaceColor }"></span>
            {{ pinnedReadout.surfaceLabel }}
          </span>
        </div>
      </div>
      <div v-if="loading" class="loading-overlay">
        <div class="spinner-border text-light" role="status">
          <span class="visually-hidden">{{ $t('shared.loading') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
  type ChartConfiguration,
  type TooltipItem,
} from 'chart.js';
import type mapboxgl from 'mapbox-gl';
import {
  MeasurementPrefUtility,
} from '../../utils/helpers/MeasurementPref';
import {
  buildElevationChartData,
  type ElevationChartData,
} from '../../utils/elevation/buildElevationChartData';
import { surfaceForValue } from '../../utils/elevation/surfaces';
import { steepnessColorForPercent } from '../../utils/elevation/steepnessGradient';
import {
  surfaceGroupForValue,
  computeSurfaceGroups,
} from '../../utils/elevation/surfaceGroups';
import { buildPinnedReadout, type PinnedReadout } from '../../utils/elevation/buildPinnedReadout';
import {
  createElevationMarker,
  type ElevationMarkerController,
} from '../../utils/elevation/elevationMarker';
import type { ProjectedHighlight } from '../../utils/elevation/projectHighlights';
import { highPointIndex } from '../../utils/elevation/highPoint';

// Explicit Chart.js v4 component registration (no `registerables`).
Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend
);

interface ColorScheme {
  line: string;
  fill: string;
  hoverLine: string;
}

interface ElevationStats {
  distanceMeters?: number;
  gainMeters?: number;
  lossMeters?: number;
  durationSeconds?: number | null;
}

const GOLD_SCHEME: ColorScheme = {
  line: '#FFDF46',
  fill: 'rgba(255, 223, 70, 0.2)',
  hoverLine: 'rgba(255, 223, 70, 0.5)',
};

const props = withDefaults(
  defineProps<{
    /** Prebuilt data from buildElevationChartData. Takes precedence over raw inputs. */
    data?: ElevationChartData | null;
    /** Raw `[lon, lat, (ele)]` coordinates (built internally if `data` absent). */
    coordinates?: number[][] | null;
    altitudes?: number[] | null;
    distances?: number[] | null;
    surfaces?: number[] | null;
    maxPoints?: number;
    measurementPref?: MeasurementPrefUtility | null;
    /**
     * Fallback line + hover colours (`fill` is retained for API compatibility
     * but unused — the area is always shaded by steepness). The line colour
     * only applies when the route lacks meaningful surface data; otherwise
     * line segments are coloured by surface group.
     */
    colorScheme?: Partial<ColorScheme> | null;
    /** Projected highlights (from projectHighlightsOntoLine) to render. */
    highlights?: ProjectedHighlight[] | null;
    collapsible?: boolean;
    collapsed?: boolean;
    expandable?: boolean;
    expanded?: boolean;
    /** Reverse sync: highlight the point at this distance (metres) from the map. */
    hoverDistanceMeters?: number | null;
    /** Optional map accessor: when provided, the marker is managed internally. */
    getMap?: (() => mapboxgl.Map | null | undefined) | null;
    title?: string;
    subtitle?: string;
    loading?: boolean;
    showStats?: boolean;
    /** Override stats shown in the header. Falls back to values from `data`. */
    stats?: ElevationStats | null;
    /**
     * Boundary indicators drawn over the profile: a dashed vertical line at
     * each `x` (in the big unit, same axis as `points`) with a small label
     * pill (e.g. the planner's day boundaries on a combined multi-day chart).
     */
    dayMarkers?: Array<{ x: number; day: number; label: string }> | null;
    /**
     * Extra tooltip line rendered above the elevation line for the hovered
     * point index (e.g. the owning route's name on a combined chart). Return
     * null/'' for no extra line.
     */
    tooltipBeforeLabel?: ((index: number) => string | null) | null;
    /**
     * Show hover stats as a small tile pinned to the top-right corner of the
     * chart instead of Chart.js's default floating tooltip. The default
     * tooltip follows the cursor and, on a compact chart, can end up sized
     * (4-5 lines) such that it covers a large stretch of the profile line
     * right where you're hovering — the whole point of hovering. Opt-in and
     * defaulted off so existing consumers (hike_map, recording crop, trail
     * overview, etc.) are unaffected; the planner turns it on.
     */
    pinnedTooltip?: boolean;
    /** Mark the profile's highest point with a small dot and its height. Off by default. */
    showHighPoint?: boolean;
  }>(),
  {
    data: null,
    coordinates: null,
    altitudes: null,
    distances: null,
    surfaces: null,
    maxPoints: 300,
    measurementPref: null,
    colorScheme: null,
    highlights: null,
    collapsible: true,
    collapsed: false,
    expandable: true,
    expanded: false,
    hoverDistanceMeters: null,
    getMap: null,
    title: '',
    subtitle: '',
    loading: false,
    showStats: true,
    stats: null,
    dayMarkers: null,
    tooltipBeforeLabel: null,
    pinnedTooltip: false,
    showHighPoint: false,
  }
);

const emit = defineEmits<{
  (e: 'hover', payload: { index: number; lngLat: [number, number]; distanceMeters: number }): void;
  (e: 'hoverEnd'): void;
  (e: 'highlightClick', highlight: ProjectedHighlight): void;
  /**
   * Emitted when a point on the chart is clicked. Additive — consumers that
   * don't listen are unaffected. Carries the sampled point `index` (aligned with
   * `data.points`) plus its map coordinate and distance, so a parent can resolve
   * the click back to route geometry (e.g. the planner's insert-control-point).
   */
  (e: 'pointClick', payload: { index: number; lngLat: [number, number]; distanceMeters: number }): void;
  (e: 'update:collapsed', value: boolean): void;
  (e: 'update:expanded', value: boolean): void;
}>();

const chartCanvas = ref<HTMLCanvasElement | null>(null);
const chartContainer = ref<HTMLElement | null>(null);
const chartInstance = shallowRef<Chart<'line'> | null>(null);

const isCollapsed = ref(props.collapsed);
const isExpanded = ref(props.expanded);
let isHoveringChart = false;
let markerController: ElevationMarkerController | null = null;

watch(() => props.collapsed, (v) => { isCollapsed.value = v; });
watch(() => props.expanded, (v) => { isExpanded.value = v; });

const measurementPref = computed(
  () => props.measurementPref ?? MeasurementPrefUtility.preference()
);

const colors = computed<ColorScheme>(() => ({ ...GOLD_SCHEME, ...(props.colorScheme ?? {}) }));

const resolvedData = computed<ElevationChartData>(() => {
  if (props.data) return props.data;
  return buildElevationChartData({
    coordinates: props.coordinates ?? [],
    altitudes: props.altitudes ?? undefined,
    distances: props.distances ?? undefined,
    surfaces: props.surfaces ?? undefined,
    maxPoints: props.maxPoints,
    measurementPref: measurementPref.value,
  });
});

const unitLabels = computed(() => resolvedData.value.unitLabels);

const resolvedStats = computed<ElevationStats>(() => {
  const d = resolvedData.value;
  return {
    distanceMeters: props.stats?.distanceMeters ?? d.maxDistanceMeters,
    gainMeters: props.stats?.gainMeters ?? d.elevationGainMeters,
    lossMeters: props.stats?.lossMeters ?? d.elevationLossMeters,
    durationSeconds: props.stats?.durationSeconds ?? null,
  };
});

// 1dp so this agrees with the hike page's stages-footer total. This is the
// readout on screen whenever elevation-panel-v2 is flagged off, so it has to
// match alongside ElevationPanelV2's own header.
const formattedDistance = computed(() =>
  measurementPref.value.bigUnitFormatter((resolvedStats.value.distanceMeters ?? 0) / 1000, true, 1)
);
const formattedGain = computed(() =>
  measurementPref.value.smallUnitFormatter(resolvedStats.value.gainMeters ?? 0, false)
);
const formattedLoss = computed(() =>
  measurementPref.value.smallUnitFormatter(resolvedStats.value.lossMeters ?? 0, false)
);
const formattedDuration = computed(() => {
  const seconds = resolvedStats.value.durationSeconds ?? 0;
  const minutes = seconds / 60;
  if (!minutes) return '0h';
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
});

// Only tracked/used in pinnedTooltip mode — see props.pinnedTooltip.
const hoveredIndex = ref<number | null>(null);

// Same facts as the Chart.js tooltip callbacks below (kept for non-pinned
// consumers), computed independently here (buildPinnedReadout.ts, pure/
// unit-testable) so pinnedTooltip mode doesn't depend on the tooltip plugin
// being enabled.
const pinnedReadout = computed<PinnedReadout | null>(() => {
  if (!props.pinnedTooltip || hoveredIndex.value == null) return null;
  return buildPinnedReadout(hoveredIndex.value, resolvedData.value, {
    unitLabels: unitLabels.value,
    tooltipBeforeLabel: props.tooltipBeforeLabel,
    hasSurfaceLine: hasSurfaceLine.value,
    fallbackLineColor: colors.value.line,
  });
});

// Colour the line by surface group only when enough of the route has known
// surface data — same ≥30%-known heuristic ElevationPanelV2 uses. Recordings
// and sparsely-tagged routes fall back to the flat scheme line colour.
const MIN_KNOWN_SURFACE_RATIO = 0.3;
const hasSurfaceLine = computed(() => {
  const surfaces = resolvedData.value.surfaces;
  if (!surfaces.length) return false;
  const groups = computeSurfaceGroups(surfaces);
  const knownCount = groups.filter((g) => g !== 'unknown').length;
  return knownCount / groups.length >= MIN_KNOWN_SURFACE_RATIO;
});

// Highlight markers positioned as a percentage of the x-axis width.
const highlightMarkers = computed(() => {
  const highlights = props.highlights;
  const maxMeters = resolvedData.value.maxDistanceMeters;
  if (!highlights || !highlights.length || maxMeters <= 0) return [];
  return highlights.map((h) => ({
    id: h.id,
    name: h.name,
    icon: h.icon,
    leftPct: Math.min(100, Math.max(0, (h.distanceMeters / maxMeters) * 100)),
    highlight: h,
  }));
});

// The canonical elevation-profile look, shared with ElevationPanelV2: the LINE
// is coloured by surface group (with a thin dark halo underneath so it stays
// legible against the fill), and the AREA beneath is shaded by steepness.
// A subtle dark casing that separates the surface-coloured line from the
// similarly-toned steepness fill beneath it.
const LINE_HALO_COLOR = 'rgba(0, 0, 0, 0.35)';
const LINE_HALO_EXTRA_WIDTH = 1.5;
const STEEPNESS_FILL_ALPHA = 0.65;

/**
 * Dataset layout: `[halo?, gradeCarrier, surfaceCarrier?, line]`. The line is
 * always LAST — other call sites (steepnessFillPlugin, the reverse-sync
 * watcher) key off `datasets.length - 1` to find it.
 *
 * `gradeCarrier`/`surfaceCarrier` are invisible (zero width, transparent)
 * datasets that exist ONLY so Chart.js's tooltip creates a SEPARATE item for
 * Grade and Surface, each with its own `labelColor` swatch — Chart.js's
 * tooltip supports one colour swatch per tooltip ITEM (i.e. per dataset), not
 * per body line, so two independently-coloured swatches (steepness colour for
 * Grade, surface-group colour for Surface) require two datasets sharing the
 * line's points.
 */
function carrierDatasetIndices(surfaceLine: boolean): { halo: number; grade: number; surface: number } {
  return {
    halo: surfaceLine ? 0 : -1,
    grade: surfaceLine ? 1 : 0,
    surface: surfaceLine ? 2 : -1,
  };
}

function buildChartConfig(): ChartConfiguration<'line'> {
  const d = resolvedData.value;
  const scheme = colors.value;
  const surfaceLine = hasSurfaceLine.value;
  const lastX = d.points.length ? d.points[d.points.length - 1].x : undefined;

  // Thin enough to read as a line over the steepness fill, not a band.
  const lineWidth = surfaceLine ? 2.5 : 2;

  const dataset: any = {
    label: 'Elevation',
    data: d.points.slice(),
    borderColor: scheme.line,
    borderWidth: lineWidth,
    pointRadius: 0,
    pointHoverRadius: 4,
    tension: 0.2,
    // The area is painted by the steepnessFill plugin instead (per-segment
    // colour keyed on grade %, which Chart.js's own `fill` can't express).
    fill: false,
    ...(surfaceLine
      ? {
          segment: {
            borderColor: (ctx: any) => {
              const surfaces = (ctx.chart.data as any).surfaces as (number | undefined)[] | undefined;
              if (!surfaces || !surfaces.length) return scheme.line;
              return surfaceGroupForValue(surfaces[ctx.p0DataIndex]).color;
            },
          },
        }
      : {}),
  };

  // Halo first so Chart.js draws it underneath the coloured line (dataset
  // draw order follows array order). Only needed when the line is
  // surface-coloured — the flat scheme line reads fine on its own.
  const haloDataset = {
    label: 'Elevation halo',
    data: d.points.slice(),
    borderColor: LINE_HALO_COLOR,
    borderWidth: lineWidth + LINE_HALO_EXTRA_WIDTH,
    pointRadius: 0,
    pointHoverRadius: 0,
    tension: 0.2,
    fill: false,
  };

  // Invisible carriers — see carrierDatasetIndices' docs above.
  const invisibleCarrier = (label: string) => ({
    label,
    data: d.points.slice(),
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    borderWidth: 0,
    pointRadius: 0,
    pointHoverRadius: 0,
    tension: 0.2,
    fill: false,
  });
  const gradeCarrierDataset = invisibleCarrier('Grade');
  const surfaceCarrierDataset = invisibleCarrier('Surface');

  const datasets = [
    ...(surfaceLine ? [haloDataset] : []),
    gradeCarrierDataset,
    ...(surfaceLine ? [surfaceCarrierDataset] : []),
    dataset,
  ];
  const { grade: gradeDatasetIndex, surface: surfaceDatasetIndex } = carrierDatasetIndices(surfaceLine);
  // The line is always the LAST dataset; the fill plugin and reverse-sync
  // watcher independently derive this same index.
  const lineDatasetIndex = datasets.length - 1;

  const chartData: any = {
    labels: d.labels.slice(),
    datasets,
    // Parallel arrays carried on data for hover → map sync and tooltips.
    lat: d.lats.slice(),
    lon: d.lons.slice(),
    distancesMeters: d.distancesMeters.slice(),
    surfaces: d.surfaces.slice(),
    gradients: d.gradients.slice(),
  };

  return {
    type: 'line',
    data: chartData,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      animation: false,
      // Room above the line for the high point's label (see highPointPlugin).
      layout: { padding: { top: props.showHighPoint ? 16 : 0 } },
      onHover: (event: any, activeElements: any[], chart: any) => {
        if (event.type === 'mouseout' || !activeElements || activeElements.length === 0) {
          handleHoverEnd();
          return;
        }
        const index = activeElements[0].index;
        handleHover(chart, index);
      },
      onClick: (_event: any, activeElements: any[], chart: any) => {
        if (!activeElements || activeElements.length === 0) return;
        handlePointClick(chart, activeElements[0].index);
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          // pinnedTooltip mode uses its own top-right readout tile instead
          // (see pinnedReadout) — Chart.js's default tooltip follows the
          // cursor and, sized to 4-5 lines, can cover a large stretch of the
          // profile line right where you're hovering.
          enabled: !props.pinnedTooltip,
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(40, 42, 50, 0.95)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: scheme.line,
          borderWidth: 1,
          padding: 8,
          displayColors: true,
          // The halo dataset duplicates the line's points (drop it); Grade and
          // Surface are kept — each is its own item so it gets its own swatch
          // (see carrierDatasetIndices).
          filter: (item: TooltipItem<'line'>) =>
            item.datasetIndex === lineDatasetIndex ||
            item.datasetIndex === gradeDatasetIndex ||
            item.datasetIndex === surfaceDatasetIndex,
          callbacks: {
            title: (items: TooltipItem<'line'>[]) =>
              `Distance: ${items[0].parsed.x} ${unitLabels.value.distance}`,
            // Only the elevation item carries the route-name prefix (overview
            // mode) — without this guard it would repeat before Grade/Surface too.
            beforeLabel: (ctx: TooltipItem<'line'>) =>
              ctx.datasetIndex === lineDatasetIndex ? (props.tooltipBeforeLabel?.(ctx.dataIndex) ?? '') : '',
            label: (ctx: TooltipItem<'line'>) => {
              if (ctx.datasetIndex === gradeDatasetIndex) {
                const gradients = (ctx.chart.data as any).gradients as number[] | undefined;
                const value = gradients ? gradients[ctx.dataIndex] : undefined;
                const rounded = Math.round(value ?? 0);
                const sign = rounded > 0 ? '+' : '';
                return `Grade: ${sign}${rounded}%`;
              }
              if (ctx.datasetIndex === surfaceDatasetIndex) {
                const surfaces = (ctx.chart.data as any).surfaces as (number | undefined)[] | undefined;
                const value = surfaces ? surfaces[ctx.dataIndex] : undefined;
                return `Surface: ${surfaceForValue(value).name}`;
              }
              return `Elevation: ${ctx.parsed.y}${unitLabels.value.elevation}`;
            },
            // Each item's swatch matches what's actually drawn in the graph
            // for that fact: Grade's swatch is the steepness-fill colour at
            // this point, Surface's is the surface-group line colour, and
            // Elevation's is the line's own rendered colour.
            labelColor: (ctx: TooltipItem<'line'>) => {
              if (ctx.datasetIndex === gradeDatasetIndex) {
                const gradients = (ctx.chart.data as any).gradients as number[] | undefined;
                const value = gradients ? gradients[ctx.dataIndex] : undefined;
                const color = steepnessColorForPercent(value ?? 0);
                return { borderColor: color, backgroundColor: color };
              }
              const surfaces = (ctx.chart.data as any).surfaces as (number | undefined)[] | undefined;
              const value = surfaces ? surfaces[ctx.dataIndex] : undefined;
              const color = surfaceLine ? surfaceGroupForValue(value).color : scheme.line;
              return { borderColor: color, backgroundColor: color };
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: lastX,
          display: true,
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 10 },
            maxTicksLimit: 8,
            callback: (v: number | string) => `${v} ${unitLabels.value.distance}`,
          },
        },
        y: {
          display: true,
          beginAtZero: false,
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
          ticks: {
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 10 },
            callback: (v: number | string) => `${v} ${unitLabels.value.elevation}`,
          },
        },
      },
    },
  };
}

// Boundary indicators (see the dayMarkers prop): dashed vertical line at each
// marker's x with a label pill at the top, truncated and clamped so labels
// never overflow the plot area. No-op when the prop is empty.
const dayMarkersPlugin = {
  id: 'dayMarkers',
  afterDatasetsDraw: (chart: Chart) => {
    const markers = props.dayMarkers;
    if (!markers || markers.length === 0) return;

    const xScale = chart.scales.x;
    const { top, bottom, left, right } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.font = '600 10px sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    for (const marker of markers) {
      const x = xScale.getPixelForValue(marker.x);
      if (Number.isNaN(x) || x < left - 1 || x > right + 1) continue;

      // Dashed boundary line — skipped at the very left edge where it would
      // just sit on the y-axis under the first label
      if (x > left + 1) {
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(255, 214, 0, 0.6)';
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Circular badge — same yellow/dark pairing (and shape) as the
      // day-list badge and the map's stage marker discs (#fddf00 / #17181d)
      // so all three read as one system. Radius grows with the label so an
      // unexpectedly long fallback string (`Day N`) still fits.
      const label = marker.label || `Day ${marker.day}`;
      const textWidth = ctx.measureText(label).width;
      const radius = Math.max(8, textWidth / 2 + 4);
      const cx = Math.max(left + radius, Math.min(x + 3 + radius, right - radius));
      const cy = top + radius;
      ctx.fillStyle = '#fddf00';
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#17181d';
      ctx.fillText(label, cx, cy + 0.5);
    }

    ctx.restore();
  },
};

// The high point (see the showHighPoint prop): a small dot on the line at the
// highest point, with its height in a pill just above it, kept inside the plot.
const highPointPlugin = {
  id: 'highPoint',
  afterDatasetsDraw: (chart: Chart) => {
    if (!props.showHighPoint) return;
    const index = highPointIndex(resolvedData.value.points);
    if (index == null) return;
    const lineIndex = chart.data.datasets.length - 1;
    const point = chart.getDatasetMeta(lineIndex).data[index] as unknown as { x: number; y: number } | undefined;
    if (!point || Number.isNaN(point.x) || Number.isNaN(point.y)) return;

    const { top, left, right } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();

    // The dot
    ctx.beginPath();
    ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#fddf00';
    ctx.stroke();

    // Its label, in the room kept above the plot, centred on the dot and
    // nudged inside the plot's edges.
    const label = `High point · ${Math.round(resolvedData.value.points[index].y)} ${unitLabels.value.elevation}`;
    ctx.font = '400 10px sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    const width = ctx.measureText(label).width;
    const cx = Math.max(left + width / 2, Math.min(point.x, right - width / 2));
    ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
    ctx.fillText(label, cx, top - 8);

    ctx.restore();
  },
};

// Shade the area under the line by steepness — per-segment colour keyed on
// grade % (the sequential |grade| scale shared with ElevationPanelV2), which
// Chart.js's built-in `fill` (single flat colour) can't express. Clips to the
// polygon under the line, then paints one rect per segment.
const steepnessFillPlugin = {
  id: 'steepnessFill',
  beforeDatasetsDraw: (chart: Chart) => {
    const lineIndex = chart.data.datasets.length - 1;
    const meta = chart.getDatasetMeta(lineIndex);
    const points = meta.data as unknown as { x: number; y: number }[];
    if (!points || points.length < 2) return;
    const gradients = (chart.data as any).gradients as number[] | undefined;
    if (!gradients || !gradients.length) return;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.lineTo(points[points.length - 1].x, bottom);
    ctx.lineTo(points[0].x, bottom);
    ctx.closePath();
    ctx.clip();

    ctx.globalAlpha = STEEPNESS_FILL_ALPHA;
    for (let i = 0; i < points.length - 1; i++) {
      const startX = points[i].x;
      const endX = points[i + 1].x;
      ctx.fillStyle = steepnessColorForPercent(gradients[i] ?? 0);
      // Slight overlap (0.5px) so adjacent segments don't leave a hairline
      // gap from sub-pixel rounding.
      ctx.fillRect(startX, top, Math.max(1, endX - startX + 0.5), bottom - top);
    }
    ctx.restore();
  },
};

const verticalLinePlugin = {
  id: 'verticalHoverLine',
  afterDraw: (chart: Chart) => {
    const active = chart.getActiveElements();
    if (!active || !active.length) return;
    const x = active[0].element.x;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chart.chartArea.top);
    ctx.lineTo(x, chart.chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = colors.value.hoverLine;
    ctx.stroke();
    ctx.restore();
  },
};

function handleHover(chart: Chart, index: number): void {
  isHoveringChart = true;
  hoveredIndex.value = index;
  const cdata = chart.data as any;
  const lat = cdata.lat?.[index];
  const lon = cdata.lon?.[index];
  const distanceMeters = cdata.distancesMeters?.[index] ?? 0;
  if (lat == null || lon == null) return;

  const lngLat: [number, number] = [lon, lat];
  if (markerController) markerController.show(lngLat);
  emit('hover', { index, lngLat, distanceMeters });
}

function handlePointClick(chart: Chart, index: number): void {
  const cdata = chart.data as any;
  const lat = cdata.lat?.[index];
  const lon = cdata.lon?.[index];
  const distanceMeters = cdata.distancesMeters?.[index] ?? 0;
  if (lat == null || lon == null) return;
  emit('pointClick', { index, lngLat: [lon, lat], distanceMeters });
}

function handleHoverEnd(): void {
  isHoveringChart = false;
  hoveredIndex.value = null;
  if (markerController) markerController.hide();
  emitHoverEnd();
}

function emitHoverEnd(): void {
  emit('hoverEnd');
}

function onHighlightClick(marker: { highlight: ProjectedHighlight }): void {
  emit('highlightClick', marker.highlight);
}

function onHighlightHover(marker: { highlight: ProjectedHighlight }): void {
  const { snappedLngLat, distanceMeters } = marker.highlight;
  if (markerController) markerController.show(snappedLngLat);
  emit('hover', { index: -1, lngLat: snappedLngLat, distanceMeters });
}

function toggleCollapse(): void {
  isCollapsed.value = !isCollapsed.value;
  emit('update:collapsed', isCollapsed.value);
  if (!isCollapsed.value) {
    nextTick(() => redrawChart());
  }
}

function toggleExpand(): void {
  isExpanded.value = !isExpanded.value;
  emit('update:expanded', isExpanded.value);
  if (isExpanded.value && isCollapsed.value) {
    isCollapsed.value = false;
    emit('update:collapsed', false);
  }
  nextTick(() => {
    if (chartInstance.value) chartInstance.value.resize();
    redrawChart();
  });
}

function destroyChart(): void {
  if (chartInstance.value) {
    try {
      chartInstance.value.stop();
    } catch {
      // no-op
    }
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
}

function redrawChart(): void {
  if (!chartCanvas.value) return;
  if (!resolvedData.value.points.length) {
    destroyChart();
    return;
  }
  const ctx = chartCanvas.value.getContext('2d');
  if (!ctx) return;

  destroyChart();
  chartInstance.value = markRaw(
    new Chart(ctx, {
      ...buildChartConfig(),
      plugins: [steepnessFillPlugin, dayMarkersPlugin, highPointPlugin, verticalLinePlugin],
    })
  );
}

// Reverse sync: highlight the point matching the map's hover distance.
watch(
  () => props.hoverDistanceMeters,
  (meters) => {
    if (isHoveringChart) return;
    const chart = chartInstance.value;
    if (!chart) return;

    if (meters == null) {
      chart.setActiveElements([]);
      chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
      chart.update('none');
      return;
    }

    const distances = (chart.data as any).distancesMeters as number[] | undefined;
    if (!distances || !distances.length) return;

    let closest = 0;
    let minDiff = Infinity;
    for (let i = 0; i < distances.length; i++) {
      const diff = Math.abs(distances[i] - meters);
      if (diff < minDiff) {
        minDiff = diff;
        closest = i;
      }
    }

    // The visible line is always the last dataset (see carrierDatasetIndices).
    // Activate it plus the grade/surface carriers so a map-triggered tooltip
    // shows the same Grade/Surface swatches a direct chart hover would.
    const lineDatasetIndex = chart.data.datasets.length - 1;
    const { grade: gradeDatasetIndex, surface: surfaceDatasetIndex } = carrierDatasetIndices(hasSurfaceLine.value);
    const activePairs = [
      { datasetIndex: lineDatasetIndex, index: closest },
      { datasetIndex: gradeDatasetIndex, index: closest },
      ...(surfaceDatasetIndex >= 0 ? [{ datasetIndex: surfaceDatasetIndex, index: closest }] : []),
    ];
    chart.setActiveElements(activePairs);
    chart.tooltip?.setActiveElements(activePairs, { x: 0, y: 0 });
    chart.update('none');
  }
);

// Rebuild the chart whenever the resolved data or styling inputs change.
watch(
  () => [resolvedData.value, colors.value, props.dayMarkers, props.showHighPoint] as const,
  () => {
    nextTick(() => redrawChart());
  },
  { deep: false }
);

onMounted(() => {
  if (props.getMap) {
    markerController = createElevationMarker(() => props.getMap?.() ?? null);
  }
  nextTick(() => redrawChart());
  // Chart.js's own `onHover` callback is gated behind `inChartArea`
  // internally, so it never fires for the mouseout event itself (the pointer
  // is by definition outside the chart area by then) — its `event.type ===
  // 'mouseout'` branch is effectively unreachable. Listen for the native
  // leave event directly so the map marker doesn't get stuck on screen.
  chartContainer.value?.addEventListener('mouseleave', handleHoverEnd);
});

onBeforeUnmount(() => {
  chartContainer.value?.removeEventListener('mouseleave', handleHoverEnd);
  destroyChart();
  if (markerController) {
    markerController.destroy();
    markerController = null;
  }
});
</script>

<style scoped>
.elevation-profile {
  display: flex;
  flex-direction: column;
  height: 180px;
  background: rgba(40, 42, 50, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  color: #fff;
  transition: height 0.3s ease;
}

.elevation-profile.collapsed {
  height: 48px;
}

.elevation-profile.expanded {
  height: 400px;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(30, 32, 38, 0.8);
  border-radius: 12px 12px 0 0;
}

.profile-title {
  /* Shrinks (rather than pushing .profile-stats / the collapse+expand
     buttons off-row) when title + subtitle don't fit a narrow panel —
     min-width: 0 is required for a flex item to shrink below its content's
     natural width at all. */
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  display: flex;
  /* stretch, not baseline: in a column flexbox anything else sizes the title and subtitle to
     their own text, so a long route name ignored the ellipsis below and ran under the stats. */
  align-items: stretch;
  flex-direction: column;
  gap: 3px;
}

.profile-title-text {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.view-subtitle {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
  font-weight: normal;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
}

/* Basis is its content, not 0: a long route title then shrinks (with an ellipsis) instead of
   running under the stats, while a short one still leaves the stats centred. Tightly spaced to
   leave the title as much room as possible. */
.profile-stats {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  /* Keep the value on one line — otherwise "11.98 km" wraps at the space
     into "11.98" / "km" on a narrow panel (single-token stats like "469m"
     never wrap, so only distance looked squashed). */
  white-space: nowrap;
}

.stat-item .material-icons {
  font-size: 14px;
  color: #ffdf46;
}

.collapse-btn,
.expand-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.collapse-btn:hover,
.expand-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.profile-content {
  flex: 1;
  position: relative;
  padding: 6px;
  overflow: hidden;
}

.chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.highlight-marker {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  cursor: pointer;
  z-index: 2;
}

.highlight-icon {
  font-size: 16px;
  color: #ffdf46;
}

.highlight-dot {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffdf46;
  border: 1px solid #fff;
}

/* pinnedTooltip mode: fixed tile, never over the hovered part of the line
   (see props.pinnedTooltip / pinnedReadout). */
.pinned-readout {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  padding: 6px 10px;
  max-width: calc(100% - 16px);
  background: rgba(40, 42, 50, 0.75);
  border: 1px solid rgba(172, 176, 191, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-size: 11px;
  color: #fff;
  pointer-events: none;
  z-index: 5;
  white-space: nowrap;
}

.pinned-readout__route {
  flex: 1 1 100%;
  font-weight: 600;
  color: #ffdf46;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pinned-readout__item {
  display: flex;
  align-items: center;
  gap: 3px;
}

.pinned-readout__item .material-icons {
  font-size: 13px;
  color: #ffdf46;
}

.pinned-readout__swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 0 0 12px 12px;
}

/* Shared elevation marker (placed on the map by createElevationMarker). */
:global(.elevation-marker) {
  display: block;
  border: 2px solid #fff;
  background-color: #ffdf46;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
</style>
